/* THE CALCULATION STEP — Max, 04/Nov/2026: "quy trình của Pricing lại thay đổi sẽ có thêm 1 step nữa".
     Linda (Costing)  the CBD and the Offer Sheet; what comes out is the BUYING COST.
     Amy              "sẽ nhập tay và canh chỉnh số phần trăm cho từng item theo như bảng trong sheet Calculation".
     Sale manager, CEO  as before.

   THE TABLE IS LINDA'S "Calculation" SHEET (27SS Mini x HX TS CBD 20261028), one row per item, with its two assumptions
   (B13 HPAC Overhead %, B14 T2 Markup %) moved onto each row — Max chose a percentage per item, and ONLY the two
   percentages change here; a wrong cost goes back to Costing.
   Every figure comes from pricingModel.js (computeSku -> computeCbd), the calculator's own arithmetic, so this table, the
   Price Approval sheet below it and the calculator cannot disagree. The selling-material fees are passed through, as the
   sheet does (`cbdCalc`). T2 is the price the quote charges — rounded up to the dollar, as the Price Approval sheet's
   N column does; the sheet's own figure before rounding is printed under it. */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { computeSku, isCbd, ruleSet } from './pricingModel';

const money = (x) => (x === null || x === undefined || !Number.isFinite(Number(x)) ? '—'
  : `$${Number(x).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (x) => (x === null || x === undefined || !Number.isFinite(Number(x)) ? '—' : `${(Number(x) * 100).toFixed(1)}%`);
const str = (v) => (v === null || v === undefined ? '' : String(v));

/** Is this quote priced the Calculation sheet's way (it went, or is going, through the new step)? */
export const isCalcQuote = (skus) => (skus || []).some((sk) => isCbd(sk?.values) && sk.values.cbdCalc);

/** The quote's SKUs with these percentages on them. */
const withPcts = (skus, pcts) => (skus || []).map((sk, i) => (isCbd(sk?.values)
  ? { ...sk, values: { ...sk.values, ohPct: pcts[i]?.oh ?? '', t2MarkupPct: pcts[i]?.mk ?? '', cbdCalc: 1 } }
  : sk));

/** What the server stores for a save: the two percentages per item, each SKU's resolved figures, and the quote's totals —
    the same shape the calculator's own save sends. */
export const calcSaveBody = (skus, rules, pcts) => {
  const live = withPcts(skus, pcts);
  const all = live.map((sk) => computeSku(sk, rules));
  return {
    items: live.map((sk, i) => ({ ohPct: pcts[i]?.oh ?? '', t2MarkupPct: pcts[i]?.mk ?? '', resolved: all[i] })),
    totals: {
      orderQty: all.reduce((a, x) => a + (x.qty || 0), 0),
      markupBase: all[0]?.markupBase ?? null,
      buyingCost: all[0]?.buyingCost ?? null,
      t1: all[0]?.t1 ?? null,
      partnerPrice: all[0]?.partnerPrice ?? null,
    },
  };
};

export default function CpCalculation({ skus, rules, editable, busy, calcBy, onLive, onSave, onSend, onReturn }) {
  const { t } = useTranslation();
  const R = ruleSet(rules);
  const fromSkus = useMemo(() => (skus || []).map((sk) => ({ oh: str(sk?.values?.ohPct), mk: str(sk?.values?.t2MarkupPct) })), [skus]);
  const fromKey = JSON.stringify(fromSkus);
  const [pcts, setPcts] = useState(fromSkus);
  // a reloaded quote (after a save) brings its own percentages back
  useEffect(() => { setPcts(JSON.parse(fromKey)); }, [fromKey]);
  const dirty = JSON.stringify(pcts) !== fromKey;
  const [open, setOpen] = useState('');        // '' | 'send' | 'return'
  const [text, setText] = useState('');

  const live = useMemo(() => withPcts(skus, pcts), [skus, pcts]);
  const liveKey = JSON.stringify(pcts);
  // the Price Approval sheet below follows what is typed here, before it is saved
  useEffect(() => { if (onLive) onLive(dirty ? live : null); }, [liveKey, dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = live.map((sk, i) => {
    if (!isCbd(sk?.values)) return null;
    const r = computeSku(sk, rules);
    const c = r.cbd || {};
    const qty = Number(r.qty) || 0;
    const base = c.split ? c.ohBase : c.base;
    const t2Raw = c.targetMargin === null || c.targetMargin === undefined
      ? c.core * (1 + c.markupPct / 100) + c.charged + (c.passFees || 0) : c.t2;
    const profit = c.t2 - r.buyingCost;
    return {
      i, name: String(sk.name || '').trim() || `SKU ${i + 1}`, color: String(sk.values.color || '').trim(), qty,
      base, frame: c.frame, oh: c.oh, moq: c.moq, sl: c.sl, fees: c.passFees || 0,
      t1: c.t1, t2: c.t2, t2Raw, profit, margin: c.t2 ? profit / c.t2 : null, ohPct: c.ohPct, mkPct: c.markupPct,
    };
  }).filter(Boolean);
  if (!rows.length) return null;
  const tot = rows.reduce((a, x) => ({
    qty: a.qty + x.qty, t1: a.t1 + x.t1 * x.qty, t2: a.t2 + x.t2 * x.qty, profit: a.profit + x.profit * x.qty,
  }), { qty: 0, t1: 0, t2: 0, profit: 0 });

  const setAt = (i, k, v) => setPcts((a) => a.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const body = () => calcSaveBody(skus, rules, pcts);
  const bad = pcts.some((x) => [x.oh, x.mk].some((v) => v !== '' && (!Number.isFinite(Number(v)) || Number(v) < 0 || Number(v) > 300)));

  const pctCell = (i, k, val, rule, label) => (editable ? (
    <span className="cpc-in">
      <input type="number" min="0" max="300" step="0.5" value={pcts[i]?.[k] ?? ''} placeholder={String(rule)}
        aria-label={`${label} · ${rows.find((x) => x.i === i)?.name || ''}`} disabled={busy}
        onChange={(e) => setAt(i, k, e.target.value)} data-cpc={`${k}-${i}`} />%
    </span>
  ) : `${Number(val).toFixed(1)}%`);

  return (
    <div className="pad-block cpc">
      <div className="pad-btitle">
        <h4>{t('pricing.calcTitle')}</h4>
        <span className="pad-kind">{editable ? t('pricing.calcKindEdit') : t('pricing.calcKind', { who: calcBy || '—' })}</span>
      </div>
      <p className="cpc-note">{t('pricing.calcNote', { oh: R.cbd_oh_pct, mk: R.cbd_t2_markup_pct })}</p>
      <div className="cpc-wrap">
        <table className="cpc-tt" data-cpc-table>
          <thead>
            <tr>
              <th className="nm">{t('pricing.calcProduct')}</th>
              <th className="n">{t('pricing.calcQty')}</th>
              <th className="n">{t('pricing.calcBase')}</th>
              <th className="n">{t('pricing.calcFrame')}</th>
              <th className="n in">{t('pricing.calcOhPct')}</th>
              <th className="n">{t('pricing.calcOh')}</th>
              <th className="n">{t('pricing.calcMoq')}</th>
              <th className="n">{t('pricing.calcSl')}</th>
              <th className="n">{t('pricing.calcFees')}</th>
              <th className="n t1">{t('pricing.calcT1')}</th>
              <th className="n">{t('pricing.calcT1Amt')}</th>
              <th className="n in">{t('pricing.calcMkPct')}</th>
              <th className="n t2">{t('pricing.calcT2')}</th>
              <th className="n">{t('pricing.calcT2Amt')}</th>
              <th className="n">{t('pricing.calcProfit')}</th>
              <th className="n">{t('pricing.calcProfitAmt')}</th>
              <th className="n">{t('pricing.calcMargin')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((x) => (
              <tr key={x.i} data-cpc-row={x.i}>
                <td className="nm"><b>{x.name}</b>{x.color ? <small>{t('pricing.calcColor', { c: x.color })}</small> : null}</td>
                <td className="n">{x.qty.toLocaleString('en-US')}</td>
                <td className="n">{money(x.base)}</td>
                <td className="n">{money(x.frame)}</td>
                <td className="n in">{pctCell(x.i, 'oh', x.ohPct, R.cbd_oh_pct, t('pricing.calcOhPct'))}</td>
                <td className="n">{money(x.oh)}</td>
                <td className="n">{x.moq ? money(x.moq) : '–'}</td>
                <td className="n">{x.sl ? money(x.sl) : '–'}</td>
                <td className="n">{money(x.fees)}</td>
                <td className="n t1"><b data-cpc-t1={x.i}>{money(x.t1)}</b></td>
                <td className="n">{money(x.t1 * x.qty)}</td>
                <td className="n in">{pctCell(x.i, 'mk', x.mkPct, R.cbd_t2_markup_pct, t('pricing.calcMkPct'))}</td>
                <td className="n t2">
                  <b data-cpc-t2={x.i}>{money(x.t2)}</b>
                  {Math.abs(x.t2Raw - x.t2) > 0.004 && <small>{t('pricing.calcT2Raw', { v: money(x.t2Raw) })}</small>}
                </td>
                <td className="n">{money(x.t2 * x.qty)}</td>
                <td className="n">{money(x.profit)}</td>
                <td className="n">{money(x.profit * x.qty)}</td>
                <td className={`n${x.margin !== null && x.margin < 0 ? ' neg' : ''}`}>{pct(x.margin)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="nm">{t('pricing.calcTotal')}</td>
              <td className="n">{tot.qty.toLocaleString('en-US')}</td>
              <td colSpan={8} />
              <td className="n">{money(tot.t1)}</td>
              <td colSpan={2} />
              <td className="n">{money(tot.t2)}</td>
              <td />
              <td className="n">{money(tot.profit)}</td>
              <td className="n">{pct(tot.t2 ? tot.profit / tot.t2 : null)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="cpc-foot">{t('pricing.calcFoot')}</p>
      {editable && (
        <div className="cpc-actions">
          {bad && <span className="cpc-bad">{t('pricing.calcBadPct')}</span>}
          <button type="button" className="sp-btn" disabled={busy || !dirty || bad} data-cpc-save
            onClick={() => onSave(body())}>{t('pricing.calcSave')}</button>
          <button type="button" className="sp-btn danger-outline" disabled={busy} data-cpc-open="return"
            onClick={() => { setOpen(open === 'return' ? '' : 'return'); setText(''); }}>{t('pricing.calcReturn')}</button>
          <button type="button" className="sp-btn primary" disabled={busy || bad} data-cpc-open="send"
            onClick={() => { setOpen(open === 'send' ? '' : 'send'); setText(''); }}>{t('pricing.calcSend')}</button>
        </div>
      )}
      {editable && open && (
        <div className={`cpc-confirm ${open}`} data-cpc-confirm={open}>
          <label htmlFor="cpc-text">{open === 'send' ? t('pricing.calcSendNote') : t('pricing.calcReturnWhy')}</label>
          <textarea id="cpc-text" rows={3} value={text} onChange={(e) => setText(e.target.value)} disabled={busy}
            placeholder={open === 'send' ? t('pricing.calcSendPh') : t('pricing.calcReturnPh')} data-cpc-text />
          <div className="cpc-actions">
            {open === 'send' && dirty && <span className="cpc-hint">{t('pricing.calcSendSaves')}</span>}
            <button type="button" className="sp-btn" disabled={busy} onClick={() => setOpen('')}>{t('pricing.calcCancel')}</button>
            {open === 'send' ? (
              <button type="button" className="sp-btn primary" disabled={busy || bad} data-cpc-go="send"
                onClick={() => onSend(text.trim(), dirty ? body() : null)}>{t('pricing.calcSendGo')}</button>
            ) : (
              <button type="button" className="sp-btn danger" disabled={busy || !text.trim()} data-cpc-go="return"
                onClick={() => onReturn(text.trim())}>{t('pricing.calcReturnGo')}</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
